import { Controller } from "@hotwired/stimulus"

// Brings a list table back to where it was scrolled when the user returns to it — from a row's
// detail page via the back button or the "‹ Mitarbeiter" link.
//
// Turbo restores only the window's scroll position, but a grid table scrolls inside its own
// container (the page around it never scrolls), so every return used to land on the first row.
// The position is kept in sessionStorage under the URL the table was rendered for (`key`, the
// request's full path): a different filter, sort or page is a different list and starts at the
// top.
//
// The offsets are recorded on every scroll and written shortly after scrolling stops. They are
// never read at disconnect: by then Turbo may already have detached the element, which reads 0.
//
//   <div class="overflow-auto" data-controller="grid-scroll-restore"
//        data-grid-scroll-restore-key-value="/employees?page=2">
const STORAGE_PREFIX = "grid-scroll:"
const WRITE_DELAY = 100

export default class extends Controller {
  static values = { key: String }

  connect() {
    this.restore()
    this.element.addEventListener("scroll", this.onScroll, { passive: true })
  }

  disconnect() {
    this.element.removeEventListener("scroll", this.onScroll)
    this.flush()
  }

  onScroll = () => {
    this.pending = { top: this.element.scrollTop, left: this.element.scrollLeft }
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.flush(), WRITE_DELAY)
  }

  flush() {
    clearTimeout(this.timer)
    if (!this.pending) return

    const position = this.pending
    this.pending = null
    try {
      window.sessionStorage.setItem(this.storageKey, JSON.stringify(position))
    } catch {
      // Storage blocked or full: the table simply starts at the top next time.
    }
  }

  restore() {
    const position = this.storedPosition()
    if (!position) return

    this.apply(position)
    // A restored Turbo snapshot can still be settling (column widths, sticky header) when the
    // controller connects; apply once more after the next frame.
    requestAnimationFrame(() => this.apply(position))
  }

  apply({ top, left }) {
    this.element.scrollTop = top
    this.element.scrollLeft = left
  }

  storedPosition() {
    try {
      const position = JSON.parse(window.sessionStorage.getItem(this.storageKey) || "null")
      if (Number.isFinite(position?.top) && Number.isFinite(position?.left)) return position
    } catch {
      // Storage blocked or a corrupt entry: nothing to restore.
    }
    return null
  }

  get storageKey() {
    return `${STORAGE_PREFIX}${this.keyValue}`
  }
}
